/**
 * Single source of truth for pricing math on the frontend.
 *
 * Why this exists: cart items in localStorage / DB carry a `customization_price`
 * field that was set the moment the user added the item. If the page that added
 * the item had a bug (or the menu data has changed since), that stored value is
 * silently wrong forever. We saw this with the Iced Americano Large bug — the
 * cart entry stored `customization_price = 29` (size 35 − discount 6), which
 * propagated stale data into Cart, Checkout, and Cashfree.
 *
 * The fix: NEVER trust `cart_item.customization_price` for math. Instead derive
 * it from the customization SPEC (size + extras chosen) plus the LIVE menu
 * options. The stored value is treated as a hint only.
 *
 * The same algorithm is mirrored in /app/backend/routers/orders.py
 * (verified_custom_price) so frontend and backend always agree.
 */

/**
 * Returns the customization price (size delta + extras) for a cart item, derived
 * from the menu item's current customization_options. Never includes the base
 * price or item-level discount.
 *
 * If the cart item has no `customization` spec, falls back to the stored
 * `customization_price` (legacy items / free coffee carry-overs).
 */
export function getCustomizationPrice(cartItem, menuItem) {
  if (!cartItem || !menuItem) return cartItem?.customization_price || 0;

  const spec = cartItem.customization;
  const opts = menuItem.customization_options;

  // No customization options on the menu item → trust legacy stored value
  if (!spec || !opts) return cartItem.customization_price || 0;

  let total = 0;

  // Size: use the size's `price` field (delta over Regular base)
  if (spec.size && Array.isArray(opts.sizes)) {
    const size = opts.sizes.find(s => s.name === spec.size);
    if (size && size.price > 0) total += size.price;
  }

  // Extras: dict format {extras: ["Espresso Shot", ...]}
  if (Array.isArray(spec.extras) && Array.isArray(opts.extras)) {
    for (const extraName of spec.extras) {
      const extra = opts.extras.find(e => e.name === extraName);
      if (extra) total += extra.price || 0;
    }
  }

  // Legacy list format: [{name, price, type?}, ...]
  if (Array.isArray(spec)) {
    for (const c of spec) {
      const name = typeof c === 'string' ? c : c?.name;
      if (!name) continue;
      const extra = opts.extras?.find(e => e.name === name);
      if (extra) total += extra.price || 0;
    }
  }

  return total;
}

/**
 * Returns the per-line charge before order-level discounts for a cart item:
 *   (menu_item.price + customization_price) * quantity − daily-offer savings
 * A daily-offer line is therefore the offer price + extras (the size is already
 * in the offer price — it used to be added again on top). Items marked
 * sub_free / free_coffee charge ONLY the customization portion.
 */
export function getLineGross(cartItem, menuItem) {
  if (!cartItem || !menuItem) return 0;
  const customPrice = getCustomizationPrice(cartItem, menuItem);
  if (cartItem.is_free_coffee || cartItem.is_sub_free) {
    return customPrice * cartItem.quantity;
  }
  return (menuItem.price + customPrice) * cartItem.quantity - getLineDailyOfferSavings(cartItem, menuItem);
}

/**
 * Per-line item-level discount amount (₹). Discount is applied on the
 * size-aware base price (so Large gets a slightly bigger ₹ discount than
 * Regular for the same percentage). Mutually exclusive with daily offer,
 * free coffee, and sub free.
 */
export function getPerUnitItemDiscount(cartItem, menuItem) {
  if (!cartItem || !menuItem) return 0;
  if (cartItem.daily_offer_id || cartItem.is_free_coffee || cartItem.is_sub_free) return 0;
  const d = menuItem.item_discount;
  if (!d || !d.discount_type || !d.discount_value) return 0;

  // Size-aware base price: matches backend orders.py logic
  let basePrice = menuItem.price;
  if (cartItem.customization && cartItem.customization.size && menuItem.customization_options?.sizes) {
    const size = menuItem.customization_options.sizes.find(s => s.name === cartItem.customization.size);
    if (size?.base_price) basePrice = size.base_price;
  }

  return d.discount_type === 'percentage'
    ? Math.round(basePrice * d.discount_value / 100)
    : Math.min(d.discount_value, basePrice);
}

export function getLineItemDiscount(cartItem, menuItem) {
  if (!cartItem) return 0;
  return getPerUnitItemDiscount(cartItem, menuItem) * cartItem.quantity;
}

/**
 * Size-aware base price for a cart line (the chosen size's `base_price`, else
 * the menu base). Matches backend orders.py `item_price` resolution and the
 * size-aware base in `getPerUnitItemDiscount`.
 */
export function getSizeAwareBasePrice(cartItem, menuItem) {
  if (!menuItem) return 0;
  let base = menuItem.price || 0;
  const size = cartItem?.customization?.size;
  if (size && menuItem.customization_options?.sizes) {
    const s = menuItem.customization_options.sizes.find(x => x.name === size);
    if (s?.base_price != null) base = s.base_price;
  }
  return base;
}

/**
 * Per-line daily-offer savings (₹) for the price breakdown. The stamped
 * `offer_price` is the discounted base for the line's CHOSEN size (set at add
 * time by ProductDetail from the size's `base_price` — Business Rule #35); the
 * saving is the size-aware base minus that offer price, × quantity. Add-ons are
 * never discounted (they're charged on top in gross). 0 when the line isn't a
 * daily-offer line. Mirrors backend orders.py: the order endpoint re-derives
 * the same offer price off the size base, so display == charge.
 */
export function getLineDailyOfferSavings(cartItem, menuItem) {
  if (!cartItem || !menuItem) return 0;
  if (!cartItem.daily_offer_id || cartItem.offer_price == null) return 0;
  const base = getSizeAwareBasePrice(cartItem, menuItem);
  return Math.max(0, base - cartItem.offer_price) * (cartItem.quantity || 1);
}
